import React, {useCallback, useEffect, useState} from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PersonIcon from "@mui/icons-material/Person";
import {Avatar, Box, Card, Chip, Grid, ListItem, ListItemAvatar, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import Markdown from "react-markdown";
import {useDispatch, useSelector} from "react-redux";
import {Link} from "react-router-dom";
import remarkGfm from "remark-gfm";
import {getPackageInternalUrl} from "../../constants/appRoutes";
import OdataGraphViewerButton from "../odata-graph-viewer-button";
import OdataViewer from "../odata-viewer";
import AiSaveView from "./AiSaveView";
import {BASE64_DATAPORTAL_LOGO, ERROR_CODE, SENDER, STATUS} from "./constants";
import {updateAiAssistantChatbotMessage} from "../../state/ai-assistant/aiAssistantAction";
import {appConfigSelector} from "../../state/app-config/appConfigSelectors";
import {nodeSelector} from "../../state/node/nodeSelectors";
import {getFormattedValue} from "../../utils/formatters";

const avatarSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: theme => theme.palette.primary.main,
  width: "40px"
};

const ChatMessage = ({
  messageIdx,
  message,
  sender,
  status,
  errorCode,
  queryInfo,
  data,
  count,
  results,
  hideAvatar,
  analyzedPartially,
  analyzedDataCount,
  isAnalyzeMessage,
  isQuickResponseMessage,
  quickResponses,
  onLoadEnd,
  onDataFetch,
  onDataFetchCalled,
  scrollToBottom
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const node = useSelector(nodeSelector);

  const appConfig = useSelector(appConfigSelector);
  const textDelay = appConfig?.aiAssistant?.textDelay ?? 0;

  const [text, setText] = useState("");

  const [viewerConfigs, setViewerConfigs] = useState({});

  useEffect(() => {
    return () => setText("");
  }, [message]);

  useEffect(() => {
    let timeoutId = null;

    if (status !== STATUS.loading) {
      setText(message);
      if (scrollToBottom) scrollToBottom();
    } else if (textDelay === 0) {
      setText(message);
      if (scrollToBottom) scrollToBottom();
      dispatch(updateAiAssistantChatbotMessage(messageIdx, {status: STATUS.success}));
      if (onLoadEnd) onLoadEnd();
    } else {
      const words = message ? message.split(" ") : [];
      let index = 0;
      let currentText = "";

      const addWord = () => {
        if (index < words.length) {
          currentText += (index === 0 ? "" : " ") + words[index];
          setText(currentText);
          if (scrollToBottom) scrollToBottom();
          index++;
          timeoutId = setTimeout(addWord, textDelay);
        } else {
          dispatch(updateAiAssistantChatbotMessage(messageIdx, {status: STATUS.success}));
          if (onLoadEnd) onLoadEnd();
        }
      };

      addWord();
    }
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [textDelay, messageIdx, message, status, onLoadEnd, scrollToBottom, dispatch]);

  const onConfigChange = useCallback(config => setViewerConfigs(prevConfig => ({...prevConfig, ...config})), []);

  const handleDataFetch = useCallback(
    (...args) => {
      if (onDataFetchCalled || !onDataFetch) return;
      dispatch(updateAiAssistantChatbotMessage(messageIdx, {onDataFetchCalled: true}));
      onDataFetch(...args);
    },
    [onDataFetchCalled, onDataFetch, dispatch, messageIdx]
  );

  return (
    <ListItem>
      <Grid
        container
        className="chat-message-container"
        sx={{
          width: "100%",
          justifyContent: sender === SENDER.user ? "flex-end" : "flex-start"
        }}
      >
        <Grid
          container
          size={9}
          alignItems="flex-start"
          flexDirection={sender === SENDER.user ? "row-reverse" : "row"}
          columnSpacing="8px"
          wrap="nowrap"
          className="chat-message"
        >
          <Grid className="chat-message__avatar">
            <ListItemAvatar sx={{minWidth: "unset"}}>
              {sender === SENDER.user ? (
                <Avatar sx={avatarSx}>
                  <PersonIcon color="primary" />
                </Avatar>
              ) : (
                <Avatar
                  src={BASE64_DATAPORTAL_LOGO}
                  alt={t("components.header.hubLogo.ariaLabel")}
                  sx={{
                    ...avatarSx,
                    visibility: hideAvatar ? "hidden" : "visible"
                  }}
                />
              )}
            </ListItemAvatar>
          </Grid>
          <Grid className="chat-message__message__container">
            {status === STATUS.pending ? (
              <Box className="chat-message__message chat-message__message--bot chat-message__message--pending">
                <Markdown remarkPlugins={[remarkGfm]}>
                  {text || t("modules.aiAssistant.chatbot.chatDialog.messages.pending")}
                </Markdown>
              </Box>
            ) : status === STATUS.error ? (
              <>
                <Box className="chat-message__message chat-message__message--bot chat-message__message--error">
                  {errorCode === ERROR_CODE.networkErr
                    ? t("modules.aiAssistant.chatbot.chatDialog.messages.error.networkErr")
                    : errorCode === ERROR_CODE.timeout
                      ? t("modules.aiAssistant.chatbot.chatDialog.messages.error.timeout")
                      : t("modules.aiAssistant.chatbot.chatDialog.messages.error.generic")}
                </Box>
                <ErrorOutlineIcon sx={{color: theme => theme.palette.error.main}} />
              </>
            ) : (
              <>
                <Box
                  className={`chat-message__message ${sender === SENDER.user ? "chat-message__message--user" : "chat-message__message--bot"} chat-message__message--success`}
                >
                  {count !== null && count !== undefined && (
                    <Typography variant="p" sx={{marginBottom: "16px"}}>
                      {t("modules.aiAssistant.chatbot.chatDialog.count.label") + " "}
                      <b>
                        {getFormattedValue(
                          count,
                          node?.decimalSeparator,
                          node?.decimalNumber,
                          "",
                          node?.roundingStrategy
                        )}
                      </b>
                    </Typography>
                  )}
                  {isAnalyzeMessage && (
                    <Typography className="chat-message__message__analysis-title" variant="h6">
                      {t("modules.aiAssistant.chatbot.chatDialog.analysis.title")}
                    </Typography>
                  )}
                  <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>
                  {status === STATUS.success && analyzedPartially && (
                    <Box
                      className="chat-message__message__partial-analysis-discaimer"
                      sx={{
                        textAlign: "end",
                        fontStyle: "italic"
                      }}
                    >
                      {t("modules.aiAssistant.chatbot.chatDialog.partialAnalysis.text", {count: analyzedDataCount})}
                    </Box>
                  )}
                  {status === STATUS.success && (results?.length || []) > 0 && (
                    <ul className="chat-message__message__results" style={{marginBottom: "16px"}}>
                      {results.map(result => (
                        <li key={result.identifier} className="chat-message__message__results__result-item">
                          <Link
                            to={getPackageInternalUrl(node.code, null, result.identifier)}
                            className="chat-message__message__results__result-link"
                          >
                            {result.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Box>
                {status === STATUS.success && queryInfo !== null && queryInfo !== undefined && (
                  <OdataGraphViewerButton
                    baseurl={queryInfo.baseurl}
                    datasource={queryInfo.datasource}
                    mainentityset={queryInfo.mainentityset}
                    query={queryInfo.query}
                  />
                )}
              </>
            )}
          </Grid>
        </Grid>
        {status === STATUS.success && data !== null && data !== undefined && (
          <Grid
            size={12}
            className="chat-message__extras"
            sx={{
              paddingLeft: "48px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start"
            }}
          >
            <Card
              variant="outlined"
              className={`chat-message__message chat-message__message--with-data`}
              sx={{
                marginTop: "16px",
                minHeight: "150px",
                height: data.viewer === "table" ? "100%" : "50vh",
                maxHeight: "50vh"
              }}
            >
              <OdataViewer
                {...data}
                config={{
                  ...data.config,
                  decimalSeparator: node?.decimalSeparator,
                  decimalNumber: node?.decimalNumber,
                  roundingStrategy: node?.roundingStrategy,
                  enableAutoSecondaryDim: true,
                  onConfigChange: onConfigChange,
                  rightActions: <AiSaveView data={data} viewerConfigs={viewerConfigs} />
                }}
                onDataFetch={handleDataFetch}
              />
            </Card>
          </Grid>
        )}
        {isQuickResponseMessage && (quickResponses || []).length > 0 && (
          <Grid
            container
            size={12}
            className="chat-message__followup"
            sx={{
              marginTop: "8px",
              paddingLeft: "48px"
            }}
            gap="8px"
          >
            {quickResponses
              .filter(action => action !== null && action !== undefined)
              .map((action, idx) => (
                <Grid key={idx}>
                  <Chip
                    label={action.label}
                    variant="contained"
                    sx={{
                      cursor: "pointer",
                      backgroundColor: theme => theme.palette.primary.main,
                      color: theme => theme.palette.primary.contrastText,
                      "&:hover": {
                        backgroundColor: theme => theme.palette.primary.light,
                        color: theme => theme.palette.primary.contrastText
                      }
                    }}
                    onClick={() => action.onClick(messageIdx)}
                  />
                </Grid>
              ))}
          </Grid>
        )}
      </Grid>
    </ListItem>
  );
};

export default ChatMessage;
